'use client'

import React from 'react'
import Link from 'next/link'
import BlogPostLayout from '@/app/components/BlogPostLayout'
import CorrectionNote from '@/app/components/blog/CorrectionNote'
import { BlogTLDR, BlogKeyTakeaways, BlogFAQ, BlogSources, BlogLastUpdated, BlogComparisonTable, BlogAppCTA, BlogWhatTheAppChecks } from '@/app/components/blog/BlogAeo'

const link = 'text-[#1E6B47] hover:text-[#155235] underline font-medium'

export default function PostContent() {
  return (
    <BlogPostLayout
      title="Best Microplastic-Free Toothpaste 2026: 18 Pastes Checked"
      author="MicroPlastics Editorial Team"
      date="May 10, 2026"
      readTime="12 min read"
      heroImage="/blog/app-search.png"
      heroImageAlt="Best microplastic-free toothpaste brands 2026 ranked"
    >
      <BlogLastUpdated iso="2026-09-21" formatted="September 21, 2026" />

      <CorrectionNote date="September 21, 2026">
        This guide previously said the EU banned microbeads in 2018 (the EU-wide ban applies from 17 October 2023), cited a 2024 &ldquo;BDJ Open&rdquo; hydroxyapatite review that does not exist (its DOI belongs to a dental implant study), and said hydroxyapatite matched fluoride in &ldquo;multiple 2024 trials&rdquo; (there are two trials, from 2021 and 2023). It also said Tom&apos;s of Maine moved to aluminium tubes (it moved from aluminium to plastic), put Dr. Bronner&apos;s in a glass jar with xylitol (it is a tube, sweetened with stevia), gave Uncle Harry&apos;s baking soda and plastic-free packaging (it has neither), offered Davids with fluoride (every Davids paste is fluoride-free), and said plastic tubes leach particles into toothpaste, which no study has tested.
      </CorrectionNote>

      <BlogTLDR>
        Plastic microbeads are banned from toothpaste in the US, England and the EU.
        What some mainstream pastes still contain are <strong>water-soluble synthetic
        polymers</strong>: in our check of nine big-brand pastes, five listed PEG-8,
        PEG-12, carbomer or a PEG/PPG copolymer, and none listed polyethylene. If you
        want none of them, these list no synthetic polymers on their own ingredient
        lists: <strong>Davids</strong> (fluoride-free, aluminium tube),{' '}
        <strong>Boka</strong> and <strong>RiseWell</strong> (hydroxyapatite),{' '}
        <strong>Tom&apos;s of Maine Whole Care</strong> (fluoride),{' '}
        <strong>Dr. Bronner&apos;s</strong>, <strong>hello kids</strong>, and{' '}
        <strong>Bite</strong> tablets in a glass jar.
      </BlogTLDR>

      <BlogKeyTakeaways
        items={[
          <>Microbeads (solid plastic scrubbing particles) are banned in toothpaste in the US, England and, since 17 October 2023, the EU.</>,
          <>The polymers left in some pastes are <strong>water-soluble</strong>: PEG-8, PEG-12, carbomer and PEG/PPG copolymers. In our 9-paste sample: Colgate Total and Colgate Kids (PEG-12), Crest Cavity Protection and Crest Kids (carbomer), Sensodyne Pronamel (PEG-8), Arm &amp; Hammer Advance White (PEG-8 and a PEG/PPG copolymer).</>,
          <>Colgate Cavity Protection, Crest Pro-Health and Tom&apos;s Kids listed none of them.</>,
          <>Hydroxyapatite pastes (Boka, RiseWell, Davids Hydroxi) are fluoride-free. Two industry-linked trials found them non-inferior to fluoride for cavities; in the US only fluoride pastes can make an anticavity claim.</>,
          <>Studies looking for actual plastic particles in toothpaste disagree: a 2026 Turkish study found none, while studies of Indian and Chinese pastes found some.</>,
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        What&apos;s in the big brands
      </h2>
      <p>
        Inactive ingredients as each maker lists them (or, where the brand site shows
        none, the US government&apos;s DailyMed label archive), checked on 21 September
        2026. Formulas change, so check the pack.
      </p>
      <BlogComparisonTable
        caption="Synthetic polymers in mainstream toothpastes (September 2026)"
        columns={[
          { key: 'product', label: 'Toothpaste' },
          { key: 'polymers', label: 'Synthetic polymers listed' },
          { key: 'source', label: 'Label source' },
        ]}
        rows={[
          { product: 'Colgate Total Active Prevention Clean Mint', polymers: 'PEG-12', source: 'colgate.com (a newer August 2026 DailyMed label lists no PEG-12: Colgate may be reformulating)' },
          { product: 'Colgate Cavity Protection', polymers: 'None', source: 'colgate.com' },
          { product: 'Colgate Kids Cavity Protection', polymers: 'PEG-12', source: 'colgate.com' },
          { product: 'Crest Cavity Protection', polymers: 'Carbomer', source: 'P&G SmartLabel' },
          { product: 'Crest Pro-Health Clean Mint', polymers: 'None', source: 'DailyMed' },
          { product: 'Crest Kids Cavity Protection', polymers: 'Carbomer', source: 'P&G SmartLabel' },
          { product: 'Sensodyne Pronamel Daily Protection', polymers: 'PEG-8', source: 'DailyMed' },
          { product: 'Arm & Hammer Advance White Extreme Whitening', polymers: 'PEG-8, PEG/PPG-116/66 copolymer', source: 'Church & Dwight ingredient disclosure' },
          { product: "Tom's of Maine Kids Silly Strawberry", polymers: 'None', source: "Tom's of Maine, DailyMed" },
        ]}
      />
      <p>
        Brand deep dives:{' '}
        <Link href="/blog/does-colgate-toothpaste-have-microplastics" className={link}>Colgate</Link>,{' '}
        <Link href="/blog/does-crest-have-microplastics" className={link}>Crest</Link> and{' '}
        <Link href="/blog/does-sensodyne-have-microplastics" className={link}>Sensodyne</Link>.
        Nearly every paste, polymer-free or not, uses cellulose gum, xanthan gum or
        carrageenan as a thickener. Those are natural or modified-natural polymers,
        not plastics.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        The polymer names to know
      </h2>
      <BlogComparisonTable
        caption="Synthetic polymers you may see on a toothpaste label"
        columns={[
          { key: 'ingredient', label: 'Ingredient' },
          { key: 'function', label: 'What it does' },
          { key: 'note', label: 'What to know' },
        ]}
        rows={[
          { ingredient: 'PEG-8, PEG-12 (polyethylene glycol)', function: 'Humectant, solvent', note: 'Water-soluble synthetic polymer; can carry trace 1,4-dioxane from manufacture' },
          { ingredient: 'Carbomer', function: 'Thickener', note: 'Cross-linked polyacrylic acid; swells into a gel in water' },
          { ingredient: 'PEG/PPG copolymers, poloxamers', function: 'Viscosity control, surfactant', note: 'Same chemical family as PEG' },
          { ingredient: 'Polyethylene (PE)', function: 'Former scrub bead', note: 'Solid plastic; banned as a microbead; listed by none of the 18 US pastes we checked' },
          { ingredient: 'Polypropylene, acrylates copolymer', function: 'Thickening, film-forming (in cosmetics)', note: 'Listed by none of the 18 US pastes we checked' },
        ]}
      />
      <p>
        More on each:{' '}
        <Link href="/microplastic-ingredients/peg" className={link}>PEG</Link>,{' '}
        <Link href="/microplastic-ingredients/carbomer" className={link}>carbomer</Link> and the{' '}
        <Link href="/blog/worst-microplastic-ingredients" className={link}>10 worst microplastic ingredients</Link>.
        Whether a dissolved polymer counts as a &ldquo;microplastic&rdquo; depends on
        who is defining it. The EU restriction covers only solid particles, while the
        Plastic Soup Foundation counts liquid and water-soluble polymers too.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Why the beads went but the polymers stayed
      </h2>
      <p>
        On <strong>18 December 2015</strong> Congress passed the{' '}
        <strong>Microbead-Free Waters Act</strong>. It defines a microbead as a solid
        plastic particle under five millimetres <em>intended to exfoliate or
        cleanse</em>, and says in so many words that rinse-off cosmetics include
        toothpaste. Cosmetic-only pastes had to stop being made by 1 July 2017 and
        sold by 1 July 2018. Fluoride toothpaste is legally an over-the-counter drug,
        so its deadlines were a year later: <strong>1 July 2018</strong> for
        manufacture and <strong>1 July 2019</strong> for sale.
      </p>
      <p>
        The Act is narrower than most people assume. It defines a microbead by its
        job, so a polymer that thickens or holds moisture is not one. It covers only
        rinse-off products. And it covers only solid particles, which leaves out the
        dissolved PEGs and carbomers still found in some pastes.
      </p>
      <p>
        England banned microbeads from rinse-off products in 2018 (manufacture from 9
        January, sale from 19 June), with no test of what the particle is for. The{' '}
        <strong>EU</strong> restriction, Regulation 2023/2055, came into force on{' '}
        <strong>17 October 2023</strong> and banned microbeads immediately, with
        other uses phased in until 2035. It covers only solid, water-insoluble
        particles, so water-soluble polymers are outside it. In the US, California
        banned rinse-off microbeads from sale from 2020 (AB 888); a 2025 bill to
        extend that to leave-on products and glitter was vetoed.
      </p>
      <p>
        The broader figure you may have seen, that 87% of products contain
        microplastics, comes from a 2022 Plastic Soup Foundation analysis of 7,704
        products from ten best-selling European cosmetics brands, including Oral-B.
        It counted liquid and water-soluble polymers, and it was not a toothpaste
        study.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Has anyone found plastic particles in toothpaste?
      </h2>
      <p>
        A few studies have looked, and they disagree. A 2026 study of personal-care
        products sold in Türkiye found <strong>no microplastics in the
        toothpastes</strong>; the bead-like particles were silica or cellulose. A 2019
        study of 20 pastes bought in Istanbul found polyethylene in 20% of them, at
        0.4&ndash;1% by weight. Studies of pastes from India (2022, 2025) and China
        (2025) reported polyethylene, polypropylene and other polymers, with methods
        that vary. None reports particles per gram for US brands. The same 2025
        Chinese study found that toothbrush wear released far more particles than
        the paste; see{' '}
        <Link href="/blog/do-toothbrush-bristles-shed-microplastics" className={link}>
          do toothbrushes shed microplastics
        </Link>
        .
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Recommended polymer-free toothpastes
      </h2>
      <p>
        Every pick below lists no synthetic polymers on the maker&apos;s own
        ingredient list. Prices are the brand&apos;s list price on 21 September 2026.
      </p>
      <BlogComparisonTable
        caption="Polymer-free toothpastes, checked against each maker's own ingredient list"
        columns={[
          { key: 'rank', label: 'Pick' },
          { key: 'brand', label: 'Brand / product' },
          { key: 'active', label: 'Active' },
          { key: 'thickener', label: 'Thickener' },
          { key: 'packaging', label: 'Packaging' },
          { key: 'price', label: 'Price' },
        ]}
        rows={[
          { rank: 'Best overall', brand: 'Davids Premium Natural', active: 'None (fluoride-free); baking soda', thickener: 'Carrageenan', packaging: 'Aluminium tube with food-grade liner, HDPE cap', price: '$10.95 / 5.25 oz' },
          { rank: 'Best hydroxyapatite', brand: 'Boka Ela Mint', active: 'Nano-hydroxyapatite', thickener: 'Xanthan gum', packaging: 'Tube (material not disclosed)', price: '$11.62 / 4 oz' },
          { rank: 'Hydroxyapatite, runner-up', brand: 'RiseWell Mineral', active: 'Hydroxyapatite', thickener: 'Cellulose gum, xanthan gum', packaging: 'LDPE tube, PP cap', price: '$12 / 3.4 oz' },
          { rank: 'Best with fluoride', brand: "Tom's of Maine Whole Care", active: 'Sodium monofluorophosphate', thickener: 'Carrageenan on older packs; none listed on the new formula', packaging: '#2 HDPE recyclable tube', price: '$7.99 / 4 oz' },
          { rank: 'Best budget', brand: "Dr. Bronner's All-One", active: 'None (fluoride-free); baking soda', thickener: 'Xanthan gum', packaging: 'Recyclable tube (material not disclosed)', price: '$7.99 / 5 oz' },
          { rank: 'Best zero-waste', brand: 'Bite Toothpaste Bits', active: 'Fluoride or nano-hydroxyapatite', thickener: 'Guar gum', packaging: 'Glass jar, aluminium lid; home-compostable refill pouch', price: '$32 / 4 months (subscription)' },
          { rank: 'Zero-waste, tablets', brand: 'Unpaste', active: 'Fluoride or nano-hydroxyapatite', thickener: 'Alginate, gum arabic', packaging: 'Kraft paper pouch with sugar-cane lining', price: 'See brand' },
          { rank: 'Clay-based', brand: "Uncle Harry's Toothpaste", active: 'None (fluoride-free)', thickener: 'Bentonite clay', packaging: 'Glass jar, plastic lid', price: '$8 / 3 oz' },
          { rank: 'Best for kids', brand: 'hello kids', active: 'Fluoride or fluoride-free', thickener: 'Xanthan gum', packaging: 'HDPE #2 tube, PP cap', price: '$5.99 / 4.2 oz' },
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Children&apos;s toothpaste
      </h2>
      <p>
        Two of the three big-brand kids&apos; pastes we checked list a synthetic
        polymer: Colgate Kids (PEG-12) and Crest Kids (carbomer). Tom&apos;s of Maine
        Kids Silly Strawberry and all three hello kids pastes we checked (fluoride
        and fluoride-free) list none; hello&apos;s &ldquo;sparkle&rdquo; paste gets
        its shimmer from mica, not plastic glitter. Ask your dentist which fluoride
        strength suits your child&apos;s age.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        The tube
      </h2>
      <p>
        Most tubes are plastic. Tom&apos;s of Maine used aluminium from the
        mid-1970s, switched to a plastic laminate in 2011, and since 2019 uses a
        recyclable #2 HDPE tube; hello uses the same kind. RiseWell&apos;s tube is
        LDPE. Davids is the exception among tubes: aluminium with a food-grade
        liner, so the paste touches the liner rather than bare metal. For no tube at
        all, Bite and Unpaste tablets come in a glass jar or a paper pouch.
      </p>
      <p>
        Whether tubes shed plastic into the paste has not been studied: we found no
        published research on it. Packaging choice is a waste question more than an
        exposure one.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Hydroxyapatite vs fluoride: what the evidence says
      </h2>
      <p>
        Hydroxyapatite is the mineral tooth enamel is made of. The first
        hydroxyapatite toothpaste launched in Japan in 1980; nano-sized particles
        came in 2003. Two randomised trials compared it with fluoride for new
        cavities. In children, a microcrystalline hydroxyapatite paste was
        non-inferior over about a year (Paszynska 2021). In adults, a
        hydroxyapatite paste was non-inferior to 1,450 ppm fluoride over 18 months
        (Paszynska 2023). Both were linked to a hydroxyapatite manufacturer, as was a
        2024 review that pooled them. A small independent 2025 review found no
        significant difference but only four eligible studies.
      </p>
      <p>
        The American Dental Association&apos;s Seal requires fluoride for a
        cavity-protection claim, and in the US only fluoride pastes can make one.
        Hydroxyapatite is a reasonable choice if you want to avoid fluoride; fluoride
        remains the better-established option.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        How to read a toothpaste label
      </h2>
      <ol className="list-decimal pl-6 space-y-2">
        <li>Find the active ingredient: sodium fluoride, stannous fluoride, sodium monofluorophosphate, hydroxyapatite, or none.</li>
        <li>Scan the inactive ingredients for PEG (any number), carbomer, PEG/PPG copolymer or poloxamer, polyethylene and acrylates.</li>
        <li>Cellulose gum, xanthan gum, guar gum and carrageenan are natural or modified-natural thickeners, not plastics.</li>
        <li>Ignore &ldquo;microbead-free&rdquo; claims: microbeads have been banned for years. The question is which polymers are left.</li>
      </ol>

      <BlogWhatTheAppChecks
        items={[
          <>Ingredient list parsed from the product label or barcode.</>,
          <>Flagged ingredients, polyethylene, acrylates, carbomer, PEG, fragrance, parabens.</>,
          <>Product category, leave-on vs rinse-off; risk weighted differently.</>,
          <>Brand and product line, clean certifications (EWG VERIFIED, MADE SAFE).</>,
          <>Cited research and regulatory references for each scan.</>,
        ]}
      />

      <BlogAppCTA
        headline="Scan personal-care products before buying"
        sub="The MicroPlastics app reads the ingredient list, flags microplastic polymers and additives, and points to cleaner alternatives in the same category."
        buttonLabel="Scan cosmetics in the app"
      />

      <BlogFAQ
        items={[
          {
            question: 'Does toothpaste still contain microplastics in 2026?',
            answer:
              'Not as microbeads: solid plastic scrubbing beads are banned in toothpaste in the US, England and the EU. Some mainstream pastes still contain water-soluble synthetic polymers such as PEG-8, PEG-12 and carbomer; in our check, five of nine big-brand pastes listed at least one, and none listed polyethylene.',
          },
          {
            question: 'Which toothpaste does not have microplastics?',
            answer:
              "These list no synthetic polymers on their makers' ingredient lists: Davids, Boka, RiseWell, Tom's of Maine Whole Care, Dr. Bronner's, Bite Toothpaste Bits, Unpaste, Uncle Harry's and hello kids. Among big brands, Colgate Cavity Protection and Crest Pro-Health listed none in September 2026.",
          },
          {
            question: 'Does Colgate toothpaste have microplastics?',
            answer:
              'Colgate Total Clean Mint and Colgate Kids list PEG-12, a water-soluble synthetic polymer, not solid microplastic particles. Colgate Cavity Protection lists none. A newer Colgate Total label from August 2026 no longer lists PEG-12, so check the pack.',
          },
          {
            question: 'What ingredients should I avoid in toothpaste?',
            answer:
              'If you want no synthetic polymers, look for PEG (any number), carbomer, PEG/PPG copolymers or poloxamers, polyethylene and acrylates. Cellulose gum, xanthan gum, guar gum and carrageenan are fine: they are natural or modified-natural thickeners.',
          },
          {
            question: 'Is hydroxyapatite toothpaste as good as fluoride?',
            answer:
              'Two randomised trials, in children (2021) and adults (2023), found hydroxyapatite non-inferior to fluoride for new cavities, but both were linked to a manufacturer and the evidence base is small. The ADA Seal requires fluoride, and in the US only fluoride pastes can claim cavity protection.',
          },
          {
            question: "Is Tom's of Maine toothpaste microplastic-free?",
            answer:
              "Tom's Whole Care lists no synthetic polymers, and none of the 24 Tom's toothpastes we checked listed one. The tube is recyclable #2 HDPE plastic; Tom's moved away from aluminium tubes in 2011.",
          },
        ]}
      />

      <BlogSources
        items={[
          { authors: 'US Congress', year: 2015, title: 'Microbead-Free Waters Act of 2015 (Public Law 114-114)', publisher: 'govinfo.gov', url: 'https://www.govinfo.gov/content/pkg/PLAW-114publ114/html/PLAW-114publ114.htm' },
          { authors: 'US Food and Drug Administration', year: 2024, title: 'The Microbead-Free Waters Act: FAQs', publisher: 'FDA', url: 'https://www.fda.gov/cosmetics/cosmetics-laws-regulations/microbead-free-waters-act-faqs' },
          { authors: 'European Commission', year: 2023, title: 'Commission Regulation (EU) 2023/2055 on synthetic polymer microparticles', publisher: 'Official Journal of the EU', url: 'https://eur-lex.europa.eu/eli/reg/2023/2055/oj' },
          { authors: 'UK Government', year: 2017, title: 'The Environmental Protection (Microbeads) (England) Regulations 2017', publisher: 'legislation.gov.uk', url: 'https://www.legislation.gov.uk/uksi/2017/1312/made' },
          { authors: 'Plastic Soup Foundation', year: 2022, title: 'Plastic: The Hidden Beauty Ingredient', publisher: 'Plastic Soup Foundation', url: 'https://www.plasticsoupfoundation.org/en/news/almost-9-in-10-products-from-major-cosmetics-brands-contain-microplastics' },
          { authors: 'Paszynska E, et al.', year: 2021, title: 'Impact of a toothpaste with microcrystalline hydroxyapatite on the occurrence of early childhood caries: a 1-year randomized clinical trial', publisher: 'Scientific Reports', url: 'https://doi.org/10.1038/s41598-021-81112-y' },
          { authors: 'Paszynska E, et al.', year: 2023, title: 'Caries-preventing effect of a hydroxyapatite-toothpaste in adults: a 18-month double-blinded randomized clinical trial', publisher: 'Frontiers in Public Health', url: 'https://doi.org/10.3389/fpubh.2023.1199728' },
          { authors: 'Chatzidimitriou K, et al.', year: 2025, title: 'The role of hydroxyapatite-based, fluoride-free toothpastes on the prevention and the remineralization of initial caries lesions: a systematic review and meta-analysis', publisher: 'Journal of Dentistry', url: 'https://doi.org/10.1016/j.jdent.2025.105691' },
          { authors: 'Bal Y, Tavşanoğlu ÜN, Başaran Kankılıç G', year: 2026, title: 'From shelf to skin: tracing microplastic contamination in cosmetics and personal care products across Türkiye', publisher: 'Bulletin of Environmental Contamination and Toxicology', url: 'https://doi.org/10.1007/s00128-026-04208-z' },
          { authors: 'Ustabasi GS, Baysal A', year: 2019, title: 'Occurrence and risk assessment of microplastics from various toothpastes', publisher: 'Environmental Monitoring and Assessment', url: 'https://doi.org/10.1007/s10661-019-7574-1' },
          { authors: 'US Food and Drug Administration', year: 2024, title: '1,4-Dioxane in cosmetics: a manufacturing byproduct', publisher: 'FDA', url: 'https://www.fda.gov/cosmetics/potential-contaminants-cosmetics/14-dioxane-cosmetics-manufacturing-byproduct' },
        ]}
      />
    </BlogPostLayout>
  )
}
